import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/api';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import {
  Crosshair, ArrowLeft, Fire, Trash, DownloadSimple, ArrowSquareOut,
  UsersThree, Clock, CurrencyDollar, ChartLineUp
} from '@phosphor-icons/react';

const QUALITY_STYLES = {
  HOT: 'bg-red-100 text-red-700', WARM: 'bg-amber-100 text-amber-800',
  COLD: 'bg-blue-100 text-blue-700', INVALID: 'bg-neutral-200 text-neutral-500',
};
const STATUSES = ['NEW', 'VERIFIED', 'CONTACTED', 'RESPONDED', 'QUOTING', 'WON', 'LOST', 'INVALID'];

const AdminLeadHunter = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [filters, setFilters] = useState({ quality: '', status: '', minScore: 0, q: '' });
  const [trade, setTrade] = useState('');
  const [cities, setCities] = useState('');
  const [busy, setBusy] = useState(null);
  const [detail, setDetail] = useState(null); // {lead, matching_contractors}

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filters.quality) params.quality = filters.quality;
      if (filters.status) params.status = filters.status;
      if (filters.minScore) params.min_score = filters.minScore;
      if (filters.q) params.q = filters.q;
      const [s, c, l, a] = await Promise.all([
        axiosInstance.get('/leadhunter/stats'),
        axiosInstance.get('/leadhunter/configs'),
        axiosInstance.get('/leadhunter/leads', { params }),
        axiosInstance.get('/leadhunter/analytics'),
      ]);
      setStats(s.data.stats);
      setConfigs(c.data.configs || []);
      setLeads(l.data.leads || []);
      setAnalytics(a.data.analytics);
    } catch (e) {
      if (e.response?.status === 403) navigate('/admin');
    }
  }, [navigate, filters]);

  useEffect(() => { load(); }, [load]);

  const createConfig = async () => {
    if (!trade.trim() || !cities.trim()) return toast({ title: 'Enter a trade and cities', variant: 'destructive' });
    setBusy('create');
    try {
      await axiosInstance.post('/leadhunter/configs', { trade, cities: [cities] });
      toast({ title: 'Hunt created' });
      setTrade(''); setCities('');
      load();
    } catch (e) {
      toast({ title: 'Create failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const runNow = async (id) => {
    setBusy(`run-${id}`);
    try {
      const r = await axiosInstance.post(`/leadhunter/configs/${id}/run-now`, {}, { timeout: 300000 });
      toast({ title: `Scanned ${r.data.found} pages → ${r.data.qualified} qualified leads` });
      load();
    } catch (e) {
      toast({ title: 'Hunt failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const removeConfig = async (id) => {
    if (!window.confirm('Delete this hunt?')) return;
    await axiosInstance.delete(`/leadhunter/configs/${id}`);
    load();
  };

  const openDetail = async (id) => {
    try {
      const r = await axiosInstance.get(`/leadhunter/leads/${id}`);
      setDetail(r.data);
    } catch (e) { toast({ title: 'Could not load lead', variant: 'destructive' }); }
  };

  const assignContractor = async (leadId, contractorId) => {
    try {
      await axiosInstance.patch(`/leadhunter/leads/${leadId}`, { assigned_contractor_id: contractorId });
      toast({ title: contractorId ? 'Lead assigned — contractor notified' : 'Assignment removed' });
      const r = await axiosInstance.get(`/leadhunter/leads/${leadId}`);
      setDetail(r.data);
      load();
    } catch (e) { toast({ title: 'Assign failed', description: e.response?.data?.detail, variant: 'destructive' }); }
  };

  const pushToOutreach = async (leadId, target) => {
    setBusy(`outreach-${target}`);
    try {
      const r = await axiosInstance.post(`/leadhunter/leads/${leadId}/send-to-outreach`, { target }, { timeout: 120000 });
      toast({
        title: target === 'homeowner'
          ? `Homeowner queued${r.data.drafted ? ' with AI draft' : ''} — review in Outreach`
          : `${r.data.added} local contractors queued in Outreach`,
      });
      const d = await axiosInstance.get(`/leadhunter/leads/${leadId}`);
      setDetail(d.data);
    } catch (e) {
      toast({ title: 'Outreach failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const setLeadStatus = async (id, status) => {
    try {
      await axiosInstance.patch(`/leadhunter/leads/${id}`, { status });
      toast({ title: `Marked ${status}` });
      setDetail(null);
      load();
    } catch (e) { toast({ title: 'Update failed', description: e.response?.data?.detail, variant: 'destructive' }); }
  };

  const exportCSV = () => {
    const head = ['Score', 'Quality', 'Status', 'Project', 'Trade', 'City', 'State', 'EstMin', 'EstMax', 'Source', 'URL', 'Summary'];
    const rows = leads.map((l) => [l.score, l.quality, l.status, l.project_type, l.trade, l.city, l.state,
      l.est_value_min, l.est_value_max, l.source, l.source_urls?.[0], (l.summary || '').replace(/"/g, "'")]);
    const csv = [head, ...rows].map((r) => r.map((v) => `"${v ?? ''}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'constructhelp-leads.csv';
    a.click();
  };

  const fmtVal = (l) => (l.est_value_min && l.est_value_max)
    ? `$${(l.est_value_min / 1000).toFixed(0)}k–$${(l.est_value_max / 1000).toFixed(0)}k` : '—';

  const statCards = stats ? [
    { label: 'Total Leads', value: stats.total, icon: UsersThree },
    { label: 'New Today', value: stats.new_today, icon: Clock },
    { label: 'Hot Leads', value: stats.hot, icon: Fire },
    { label: 'Pipeline Value', value: `$${(stats.pipeline_value || 0).toLocaleString()}`, icon: CurrencyDollar },
    { label: 'Conversion', value: `${stats.conversion_rate}%`, icon: ChartLineUp },
  ] : [];

  return (
    <div className="min-h-screen bg-neutral-100" data-testid="lead-hunter-page">
      <div className="bg-neutral-900 border-b-4 border-[#D95800]">
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center gap-4">
          <button onClick={() => navigate('/admin')} className="text-white hover:text-[#D95800]" data-testid="hunter-back-btn">
            <ArrowLeft size={24} weight="bold" />
          </button>
          <div className="w-12 h-12 bg-[#D95800] border-2 border-white flex items-center justify-center">
            <Crosshair size={26} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-white uppercase">AI Lead Hunter</h1>
            <p className="text-neutral-400 text-sm">Finds public homeowner requests for construction work, scores them, and alerts you on HOT leads</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="hunter-stats">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white border-2 border-black p-4 shadow-[3px_3px_0px_#000]">
              <s.icon size={20} weight="bold" className="text-[#D95800]" />
              <p className="text-xl font-black mt-1">{s.value}</p>
              <p className="text-xs font-bold uppercase text-neutral-500">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Analytics */}
        {analytics && analytics.top_trades.length > 0 && (
          <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_#000]" data-testid="hunter-analytics">
            <h2 className="font-black uppercase text-lg mb-1">Hunt Analytics</h2>
            <p className="text-sm text-neutral-500 mb-4">Which trades & cities produce the most HOT leads — use this to aim your hunts.</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[{ title: 'Top Trades', rows: analytics.top_trades, testId: 'top-trades' },
                { title: 'Top Cities', rows: analytics.top_cities, testId: 'top-cities' }].map(({ title, rows, testId }) => {
                const max = Math.max(...rows.map((r) => r.total), 1);
                return (
                  <div key={testId} data-testid={testId}>
                    <h3 className="text-xs font-black uppercase text-neutral-400 mb-2">{title}</h3>
                    <div className="space-y-2">
                      {rows.slice(0, 6).map((r) => (
                        <div key={r.name}>
                          <div className="flex items-center justify-between text-xs mb-0.5">
                            <span className="font-bold capitalize truncate">{r.name}</span>
                            <span className="text-neutral-500 shrink-0 ml-2">
                              <span className="text-red-600 font-black">{r.hot} HOT</span> · {r.total} leads · {r.hot_rate}% hot · avg {r.avg_score}
                            </span>
                          </div>
                          <div className="w-full bg-neutral-100 h-3 flex">
                            <div className="h-3 bg-red-500" style={{ width: `${(r.hot / max) * 100}%` }} />
                            <div className="h-3 bg-amber-400" style={{ width: `${(r.warm / max) * 100}%` }} />
                            <div className="h-3 bg-neutral-300" style={{ width: `${((r.total - r.hot - r.warm) / max) * 100}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              <div data-testid="hunt-efficiency">
                <h3 className="text-xs font-black uppercase text-neutral-400 mb-2">Hunt Efficiency (qualified / scanned, last run)</h3>
                {analytics.hunts.slice(0, 5).map((h) => (
                  <div key={h.id} className="flex items-center justify-between text-xs py-1.5 border-b border-neutral-100">
                    <span className="font-bold capitalize truncate">{h.trade} <span className="text-neutral-400 font-medium">· {h.cities.join(', ')}</span></span>
                    <span className="shrink-0 ml-2">
                      <span className={`font-black ${h.efficiency >= 25 ? 'text-emerald-600' : h.efficiency > 0 ? 'text-amber-600' : 'text-neutral-400'}`}>{h.efficiency}%</span>
                      <span className="text-neutral-500"> · {h.total_qualified} total</span>
                      {!h.active && <span className="ml-1 px-1 bg-neutral-200 font-bold">PAUSED</span>}
                    </span>
                  </div>
                ))}
              </div>
              <div data-testid="lead-trend">
                <h3 className="text-xs font-black uppercase text-neutral-400 mb-2">Leads — last 14 days</h3>
                <div className="flex items-end gap-1 h-20">
                  {analytics.trend.map((t) => {
                    const tmax = Math.max(...analytics.trend.map((x) => x.total), 1);
                    return (
                      <div key={t.day} className="flex-1 flex flex-col items-center justify-end h-full" title={`${t.day}: ${t.total} leads (${t.hot} hot)`}>
                        <div className="w-full bg-red-500" style={{ height: `${(t.hot / tmax) * 100}%` }} />
                        <div className="w-full bg-neutral-300" style={{ height: `${((t.total - t.hot) / tmax) * 100}%` }} />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-[9px] text-neutral-400 mt-1">
                  <span>{analytics.trend[0]?.day}</span><span>{analytics.trend[analytics.trend.length - 1]?.day}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Hunts */}
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_#000]">
          <h2 className="font-black uppercase text-lg mb-1">Hunts</h2>
          <p className="text-sm text-neutral-500 mb-4">A hunt = one trade across one or more cities (comma-separated). Runs automatically every 24h; use Run Now anytime.</p>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <input value={trade} onChange={(e) => setTrade(e.target.value)} placeholder="Trade (e.g. roofing)"
              className="h-11 px-3 border-2 border-black font-bold w-48" data-testid="hunt-trade-input" />
            <input value={cities} onChange={(e) => setCities(e.target.value)} placeholder="Cities (e.g. Newark NJ, Hoboken NJ)"
              className="h-11 px-3 border-2 border-black font-bold flex-1 min-w-[240px]" data-testid="hunt-cities-input" />
            <Button onClick={createConfig} disabled={busy === 'create'}
              className="h-11 bg-[#D95800] hover:bg-[#E55D00] text-white font-black uppercase border-2 border-black shadow-[3px_3px_0px_#000] rounded-none"
              data-testid="create-hunt-btn">
              {busy === 'create' ? 'Creating…' : 'Add Hunt'}
            </Button>
          </div>
          {configs.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-3 border-t border-neutral-200 py-2" data-testid={`hunt-row-${c.id}`}>
              <div className="flex-1 min-w-[200px]">
                <span className="font-bold capitalize">{c.trade}</span>
                <span className="text-neutral-500 text-sm"> · {c.cities.join(', ')}</span>
                <p className="text-xs text-neutral-400">
                  last run: {c.last_run_at ? new Date(c.last_run_at).toLocaleString() : 'never'} · found {c.last_found} · qualified {c.last_qualified} · total {c.total_qualified}
                </p>
              </div>
              <button onClick={() => runNow(c.id)} disabled={busy === `run-${c.id}`}
                className="px-3 py-1.5 text-xs font-bold bg-neutral-900 text-white border border-black rounded"
                data-testid={`run-hunt-btn-${c.id}`}>
                {busy === `run-${c.id}` ? 'Hunting… (~1 min)' : 'Run Now'}
              </button>
              <button onClick={() => removeConfig(c.id)} className="px-2 py-1.5 text-xs font-bold bg-red-50 border border-red-300 text-red-600 rounded">
                <Trash size={14} className="inline" />
              </button>
            </div>
          ))}
        </div>

        {/* Filters + table */}
        <div className="bg-white border-2 border-black shadow-[4px_4px_0px_#000]">
          <div className="p-4 flex flex-wrap items-center gap-3 border-b-2 border-neutral-200">
            <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              placeholder="Search leads…" className="h-10 px-3 border-2 border-neutral-300 font-medium w-52" data-testid="lead-search-input" />
            <select value={filters.quality} onChange={(e) => setFilters((f) => ({ ...f, quality: e.target.value }))}
              className="h-10 px-2 border-2 border-neutral-300 font-bold" data-testid="quality-filter">
              <option value="">All quality</option>
              {['HOT', 'WARM', 'COLD'].map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              className="h-10 px-2 border-2 border-neutral-300 font-bold" data-testid="status-filter">
              <option value="">All statuses</option>
              {STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            <select value={filters.minScore} onChange={(e) => setFilters((f) => ({ ...f, minScore: Number(e.target.value) }))}
              className="h-10 px-2 border-2 border-neutral-300 font-bold" data-testid="score-filter">
              <option value={0}>Any score</option>
              {[50, 70, 85].map((x) => <option key={x} value={x}>Score ≥ {x}</option>)}
            </select>
            <div className="flex-1" />
            <button onClick={exportCSV} className="h-10 px-4 text-sm font-bold bg-neutral-900 text-white border-2 border-black rounded-none flex items-center gap-2" data-testid="export-csv-btn">
              <DownloadSimple size={16} weight="bold" /> Export CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="bg-neutral-900 text-white text-left">
                  {['Score', 'Quality', 'Project', 'Trade', 'Location', 'Est. Value', 'Source', 'Status'].map((h) => (
                    <th key={h} className="p-3 font-black uppercase text-xs">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr><td colSpan={8} className="p-8 text-center text-neutral-400 font-bold">
                    No leads yet — add a hunt above and hit Run Now
                  </td></tr>
                )}
                {leads.map((l) => (
                  <tr key={l.id} onClick={() => openDetail(l.id)} className="border-t border-neutral-200 hover:bg-orange-50 cursor-pointer" data-testid={`lead-row-${l.id}`}>
                    <td className="p-3 font-black">{l.score}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 text-xs font-black rounded ${QUALITY_STYLES[l.quality] || ''}`}>{l.quality}</span></td>
                    <td className="p-3 font-bold">{l.project_type || '—'}</td>
                    <td className="p-3 capitalize">{l.trade}</td>
                    <td className="p-3">{[l.city, l.state].filter(Boolean).join(', ') || '—'}</td>
                    <td className="p-3">{fmtVal(l)}</td>
                    <td className="p-3 text-xs">{l.source}</td>
                    <td className="p-3"><span className="px-2 py-0.5 text-xs font-black bg-neutral-100 rounded">{l.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-xs text-neutral-400">
          Only publicly accessible pages are scanned — no logins, CAPTCHAs, or paywalls are bypassed. Value estimates are AI approximations, not guaranteed prices.
        </p>
      </div>

      {/* Lead detail modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white border-2 border-black shadow-[8px_8px_0px_#000] max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="lead-detail-modal">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <span className={`px-2 py-0.5 text-xs font-black rounded ${QUALITY_STYLES[detail.lead.quality]}`}>{detail.lead.quality}</span>
                <h2 className="text-xl font-black uppercase mt-2">{detail.lead.project_type || detail.lead.trade}</h2>
                <p className="text-sm text-neutral-500">{[detail.lead.city, detail.lead.state, detail.lead.zip].filter(Boolean).join(', ')}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-black text-[#D95800]" data-testid="detail-score">{detail.lead.score}</p>
                <p className="text-[10px] font-black uppercase text-neutral-400">/100</p>
              </div>
            </div>
            <div className="w-full bg-neutral-200 h-2 mb-5"><div className="h-2 bg-[#D95800]" style={{ width: `${detail.lead.score}%` }} /></div>

            <p className="text-xs font-black uppercase text-neutral-400 mb-1">AI Summary</p>
            <p className="text-sm mb-4">{detail.lead.summary}</p>
            <p className="text-xs font-black uppercase text-neutral-400 mb-1">AI Analysis</p>
            <p className="text-sm mb-2">{detail.lead.reasoning}</p>
            <ul className="text-xs text-neutral-500 list-disc pl-5 mb-4">
              {(detail.lead.score_reasons || []).map((r, i) => <li key={i}>{r}</li>)}
            </ul>
            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <p><span className="font-bold">Est. value:</span> {fmtVal(detail.lead)}</p>
              <p><span className="font-bold">Urgency:</span> {detail.lead.urgency}</p>
              <p><span className="font-bold">Source:</span> {detail.lead.source}</p>
              <p><span className="font-bold">Contact:</span> {detail.lead.contact_info || 'Not public'}</p>
            </div>
            <p className="text-xs font-black uppercase text-neutral-400 mb-1">Original request (excerpt)</p>
            <p className="text-xs text-neutral-500 bg-neutral-50 border p-3 mb-4 max-h-28 overflow-y-auto">{detail.lead.description_excerpt}</p>

            <p className="text-xs font-black uppercase text-neutral-400 mb-1">
              {detail.matching_contractors.length} matching contractors on ConstructHelp — click to assign
            </p>
            <div className="flex flex-wrap gap-2 mb-5" data-testid="matching-contractors">
              {detail.matching_contractors.map((c) => {
                const assigned = detail.lead.assigned_contractor_id === c.id;
                return (
                  <button key={c.id} onClick={() => assignContractor(detail.lead.id, assigned ? '' : c.id)}
                    className={`px-2 py-1 text-xs font-bold rounded border-2 transition-colors ${assigned ? 'bg-[#D95800] text-white border-black' : 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200'}`}
                    data-testid={`assign-contractor-${c.id}`}>
                    {assigned ? '✓ Assigned: ' : ''}{c.name} · {c.trade || 'contractor'}
                  </button>
                );
              })}
            </div>

            <p className="text-xs font-black uppercase text-neutral-400 mb-1">AI Outreach</p>
            <div className="flex flex-wrap items-center gap-2 mb-5 bg-neutral-50 border border-neutral-200 p-3" data-testid="lead-outreach-block">
              {detail.lead.outreach?.homeowner_prospect_id ? (
                <span className="px-2 py-1 text-xs font-bold bg-emerald-100 text-emerald-700 rounded" data-testid="outreach-homeowner-queued">✓ Homeowner queued</span>
              ) : (
                <button onClick={() => pushToOutreach(detail.lead.id, 'homeowner')} disabled={busy === 'outreach-homeowner'}
                  className="h-9 px-3 text-xs font-bold bg-[#D95800] text-white border-2 border-black shadow-[2px_2px_0px_#000] hover:bg-[#E55D00]"
                  data-testid="outreach-homeowner-btn">
                  {busy === 'outreach-homeowner' ? 'Drafting…' : 'Invite Homeowner'}
                </button>
              )}
              <button onClick={() => pushToOutreach(detail.lead.id, 'contractors')} disabled={busy === 'outreach-contractors'}
                className="h-9 px-3 text-xs font-bold bg-neutral-900 text-white border-2 border-black shadow-[2px_2px_0px_#000]"
                data-testid="outreach-contractors-btn">
                {busy === 'outreach-contractors' ? 'Searching… (~30s)' : 'Recruit Local Contractors'}
              </button>
              {detail.lead.outreach?.contractors_queued > 0 && (
                <span className="px-2 py-1 text-xs font-bold bg-emerald-100 text-emerald-700 rounded" data-testid="outreach-contractors-queued">
                  ✓ {detail.lead.outreach.contractors_queued} contractors queued
                </span>
              )}
              <button onClick={() => navigate('/admin/outreach')} className="ml-auto text-xs font-bold text-[#D95800] underline" data-testid="open-outreach-link">
                Open Outreach →
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              <a href={detail.lead.source_urls?.[0]} target="_blank" rel="noreferrer"
                className="h-10 px-4 flex items-center gap-2 text-sm font-bold bg-neutral-900 text-white border-2 border-black" data-testid="open-source-btn">
                <ArrowSquareOut size={16} /> Open Source
              </a>
              {['CONTACTED', 'QUOTING', 'WON', 'INVALID'].map((s) => (
                <button key={s} onClick={() => setLeadStatus(detail.lead.id, s)}
                  className="h-10 px-3 text-sm font-bold bg-neutral-100 hover:bg-neutral-200 border-2 border-neutral-300"
                  data-testid={`mark-${s.toLowerCase()}-btn`}>
                  Mark {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLeadHunter;
